import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Heart, MessageSquare, Share2, ChevronUp, Play, Volume2, VolumeX } from "lucide-react";
import { tapHaptic } from "../lib/haptics";
import { toast } from "sonner";
import SourcePlatformBadge from "../components/SourcePlatformBadge";

export default function Shorts() {
  const [shorts, setShorts] = useState([]);
  const [idx, setIdx] = useState(0);
  const nav = useNavigate();

  useEffect(() => { api.get("/videos/shorts").then((r) => setShorts(r.data)); }, []);

  useEffect(() => {
    const onWheel = (e) => {
      if (e.deltaY > 30) setIdx((i) => Math.min(shorts.length - 1, i + 1));
      else if (e.deltaY < -30) setIdx((i) => Math.max(0, i - 1));
    };
    const onKey = (e) => {
      if (e.key === "ArrowDown") setIdx((i) => Math.min(shorts.length - 1, i + 1));
      if (e.key === "ArrowUp") setIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("wheel", onWheel); window.removeEventListener("keydown", onKey); };
  }, [shorts.length]);

  // Touch swipe
  const touchStart = useRef(null);
  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientY; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const diff = touchStart.current - e.changedTouches[0].clientY;
    if (diff > 50) { tapHaptic(); setIdx((i) => Math.min(shorts.length - 1, i + 1)); }
    else if (diff < -50) { tapHaptic(); setIdx((i) => Math.max(0, i - 1)); }
    touchStart.current = null;
  };

  if (shorts.length === 0) {
    return (
      <div data-testid="shorts-empty" className="pt-32 pb-20 min-h-screen flex items-center justify-center text-white/60">
        No shorts available yet — check back soon.
      </div>
    );
  }

  const current = shorts[idx];
  return (
    <div
      data-testid="shorts-page"
      className="fixed inset-0 bg-black pt-16 md:pt-20 pb-20 md:pb-0"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="h-full flex items-center justify-center relative">
        <ShortPlayer key={current.id} video={current} onEnded={() => setIdx((i) => Math.min(shorts.length - 1, i + 1))} />
        {idx < shorts.length - 1 && (
          <button onClick={() => setIdx(idx + 1)} data-testid="short-next-btn" className="absolute bottom-8 left-1/2 -translate-x-1/2 tv-glass rounded-full w-10 h-10 flex items-center justify-center text-white/80 md:hidden">
            <ChevronUp className="w-5 h-5 rotate-180" />
          </button>
        )}
      </div>
    </div>
  );
}

function ShortPlayer({ video, onEnded }) {
  const ref = useRef(null);
  const musicRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [likes, setLikes] = useState(video.like_count || 0);

  const toggleMute = () => {
    const v = ref.current;
    const m = musicRef.current;
    if (v) { v.muted = !v.muted; setMuted(v.muted); }
    if (m) m.muted = v ? v.muted : !muted;
  };
  const like = async () => {
    try {
      tapHaptic();
      const r = await api.post(`/likes/${video.id}`);
      setLikes((l) => l + (r.data.liked ? 1 : -1));
    } catch { toast.error("Sign in to like"); }
  };
  const share = () => { navigator.clipboard.writeText(`${window.location.origin}/watch/${video.id}`); toast.success("Link copied"); };

  return (
    <div className="relative h-full w-full max-w-md mx-auto flex items-center justify-center">
      <video
        ref={ref}
        src={video.video_url}
        poster={video.thumbnail_url}
        autoPlay
        muted={muted || !!video.music?.preview_url}
        playsInline
        loop={false}
        onEnded={onEnded}
        className="h-full w-full object-contain bg-black"
        onClick={toggleMute}
        data-testid="short-video"
      />
      {video.music?.preview_url && (
        <audio ref={musicRef} src={video.music.preview_url} autoPlay loop muted={muted} data-testid={`short-music-audio-${video.id}`} />
      )}
      {video.format === "story" && (
        <div
          data-testid={`short-story-badge-${video.id}`}
          className="absolute top-20 left-3 z-10 bg-gradient-to-r from-fuchsia-500 to-orange-400 text-white text-[10px] font-black uppercase tracking-[0.2em] px-2 py-1 rounded-full shadow"
        >✦ Story</div>
      )}
      {video.source_platform && (
        <div className="absolute top-20 right-3 z-10" data-testid={`short-source-${video.id}`}>
          <SourcePlatformBadge platform={video.source_platform} label={video.source_platform_label} />
        </div>
      )}
      {video.music && (
        <div className="absolute bottom-24 left-3 right-16 flex items-center gap-2 bg-black/60 backdrop-blur rounded-full px-3 py-1.5 w-max max-w-full" data-testid={`short-music-${video.id}`}>
          {video.music.album_art && <img src={video.music.album_art} alt="" className="w-5 h-5 rounded object-cover" />}
          <div className="text-white text-[11px] font-semibold truncate max-w-[160px]">♪ {video.music.title}</div>
          <div className="text-white/60 text-[10px] truncate max-w-[100px]">{video.music.artist}</div>
        </div>
      )}
      <div className="absolute bottom-20 left-3 right-16 text-white">
        <Link to={`/creator/${video.creator_id}`} className="flex items-center gap-2">
          <img src={video.creator_avatar} className="w-9 h-9 rounded-full border border-white/30 object-cover" alt="" />
          <div>
            <div className="font-semibold text-sm flex items-center gap-1">
              {video.creator_name}
              {video.creator_verified && <span className="text-tv-brand">✓</span>}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-white/70">{video.category}</div>
          </div>
        </Link>
        <div className="mt-2 font-display font-bold text-lg leading-snug">{video.title}</div>
        <div className="text-xs text-white/70 line-clamp-2 mt-1">{video.description}</div>
      </div>
      <div className="absolute right-3 bottom-24 flex flex-col items-center gap-4">
        <button onClick={like} className="flex flex-col items-center text-white" data-testid="short-like">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center"><Heart className="w-6 h-6" /></div>
          <div className="text-xs mt-1">{likes}</div>
        </button>
        <button onClick={share} className="flex flex-col items-center text-white" data-testid="short-share">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center"><Share2 className="w-6 h-6" /></div>
          <div className="text-xs mt-1">Share</div>
        </button>
        <button onClick={toggleMute} className="flex flex-col items-center text-white">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">{muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}</div>
        </button>
        <Link to={`/watch/${video.id}`} className="flex flex-col items-center text-white">
          <div className="w-12 h-12 rounded-full bg-tv-accent text-black flex items-center justify-center"><Play className="w-5 h-5 fill-current" /></div>
          <div className="text-xs mt-1">Full</div>
        </Link>
      </div>
    </div>
  );
}
